import { AuthenticatedSession } from '@/authenticatedSession/authenticatedSession';
import { FirmDocument, MatterDocument, UserDocument } from '@shared/types';
import { getEnrichedMatterContacts } from './getEnrichedMatterContacts';
import { EnrichedMatterContact, FlattenedMatter } from './aggregateTypes';
import { makeFlattenedMatter } from './makeFlattenedMatter';

export async function getFlattenedMatter(props: {
  authSession: AuthenticatedSession;
  matterId: string;
}): Promise<FlattenedMatter | undefined> {
  const { authSession, matterId } = props;
  const matterSnapshot = await authSession.db.getMatter(matterId);
  const matterData = matterSnapshot.data();
  if (!matterData) {
    return undefined;
  }
  const matter: MatterDocument = {
    ...matterData,
    id: matterSnapshot.id,
  };

  if (!matter.responsibleAttorney) {
    return undefined;
  }
  const responsibleAttorneySnapshot = await authSession.db.getUser(
    matter.responsibleAttorney,
  );
  const responsibleAttorneyData = responsibleAttorneySnapshot.data();
  if (!responsibleAttorneyData) {
    return undefined;
  }
  const responsibleAttorney: UserDocument = {
    ...responsibleAttorneyData,
    id: responsibleAttorneySnapshot.id,
  };

  const firmSnapshot = await authSession.db.getFirm();
  const firmData = firmSnapshot.data();
  if (!firmData) {
    return undefined;
  }
  const firm: FirmDocument = {
    ...firmData,
    id: firmSnapshot.id,
  };

  const contacts = await getEnrichedMatterContacts(matterId, authSession.db);

  let otherAssignee: UserDocument | undefined;
  if (matter.otherAssignee) {
    const otherAssigneeSnapshot = await authSession.db.getUser(
      matter.otherAssignee,
    );
    const otherAssigneeData = otherAssigneeSnapshot.data();
    if (!otherAssigneeData) {
      return undefined;
    }
    otherAssignee = { ...otherAssigneeData, id: otherAssigneeSnapshot.id };
  }

  let engagementOriginator: UserDocument | undefined;
  if (matter.engagementOriginator) {
    const engagementOriginatorSnapshot = await authSession.db.getUser(
      matter.engagementOriginator,
    );
    const engagementOriginatorData = engagementOriginatorSnapshot.data();
    if (!engagementOriginatorData) {
      return undefined;
    }
    engagementOriginator = {
      ...engagementOriginatorData,
      id: engagementOriginatorSnapshot.id,
    };
  }

  let referralOriginator: UserDocument | undefined;
  if (matter.referralOriginator) {
    const referralOriginatorSnapshot = await authSession.db.getUser(
      matter.referralOriginator,
    );
    const referralOriginatorData = referralOriginatorSnapshot.data();
    if (!referralOriginatorData) {
      return undefined;
    }
    referralOriginator = {
      ...referralOriginatorData,
      id: referralOriginatorSnapshot.id,
    };
  }

  let primaryClient: EnrichedMatterContact | undefined;
  if (matter.primaryClientId) {
    primaryClient = contacts.find(
      (contact) => contact.id === matter.primaryClientId,
    );
  }

  let primaryAdverseParty: EnrichedMatterContact | undefined;
  if (matter.primaryAdversePartyId) {
    primaryAdverseParty = contacts.find(
      (contact) => contact.id === matter.primaryAdversePartyId,
    );
  }

  const engagementLettersSnapshot = await authSession.db.getEngagementLetters([
    ['matterId', '==', matterId],
  ]);
  const engagementLetters = engagementLettersSnapshot.docs.map((doc) => ({
    ...doc.data(),
    id: doc.id,
  }));

  const insuranceCasesSnapshot = await authSession.db.getInsuranceCases([
    ['matterId', '==', matterId],
  ]);
  const insuranceCases = insuranceCasesSnapshot.docs.map((doc) => ({
    ...doc.data(),
    id: doc.id,
  }));

  return makeFlattenedMatter(
    {
      matter,
      firm,
      contacts,
      responsibleAttorney,
      otherAssignee,
      engagementOriginator,
      referralOriginator,
      primaryClient,
      primaryAdverseParty,
      engagementLetters,
      insuranceCases,
    },
    authSession.db,
  );
}
